import type { ReactNode, RefObject } from "react";
import FormActionButton from "../formActionButton/formActionButton";
import PillTitle from "../pillTitle/pillTitle";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import "./resultHeader.scss";

export type ResultHeaderProps = {
  pillText: string;
  subtitle?: ReactNode;

  // ref on the actions container (for click-outside detection in useResultExport)
  actionsRef?: RefObject<HTMLDivElement | null>;

  // Export
  isExportMenuOpen?: boolean;
  isExporting?: boolean;
  exportFormat?: "pdf" | "png" | null;
  isExportDisabled?: boolean;
  onToggleExportMenu?: () => void;
  onExport?: (format: "pdf" | "png") => void;

  // Analysis
  showAnalysisButton?: boolean;
  isAnalysisDisabled?: boolean;
  onAnalyze?: () => void;

  // Email
  onEmailResult?: () => void;

  // Extra slot — arbitrary actions rendered after the standard buttons
  extraActions?: ReactNode;
};

export default function ResultHeader({
  pillText,
  subtitle,
  actionsRef,
  isExportMenuOpen,
  isExporting,
  exportFormat,
  isExportDisabled,
  onToggleExportMenu,
  onExport,
  showAnalysisButton,
  isAnalysisDisabled,
  onAnalyze,
  onEmailResult,
  extraActions,
}: ResultHeaderProps) {
  const { t } = useI18n();

  const hasActions =
    onToggleExportMenu != null || showAnalysisButton || extraActions != null;

  return (
    <div className="result-header">
      <PillTitle variant="blue-brighter" text={pillText} />

      {/* h2 + buttons on one row */}
      <div className="result-header__row">
        <h2 className="mb-0">{t("Wynik badania")}</h2>

        {hasActions && (
          <div className="result-header__actions" ref={actionsRef}>
            {onToggleExportMenu && (
              <div className="result-header__btn-wrap">
                <FormActionButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  iconClassName="bi bi-download"
                  onClick={onToggleExportMenu}
                  disabled={isExportDisabled}
                  loading={isExporting}
                  loadingLabel={
                    exportFormat === "pdf"
                      ? t("Eksportowanie PDF…")
                      : t("Eksportowanie PNG…")
                  }
                  aria-expanded={isExportMenuOpen}
                  aria-haspopup="menu"
                >
                  {t("Eksportuj")}
                </FormActionButton>

                {isExportMenuOpen && (
                  <div
                    className="result-header__export-menu"
                    role="menu"
                    aria-label={t("Eksportuj wykres")}
                  >
                    <button
                      type="button"
                      className="result-header__export-option"
                      onClick={() => onExport?.("pdf")}
                      role="menuitem"
                    >
                      PDF
                    </button>
                    <button
                      type="button"
                      className="result-header__export-option"
                      onClick={() => onExport?.("png")}
                      role="menuitem"
                    >
                      PNG
                    </button>
                    {onEmailResult && (
                      <button
                        type="button"
                        className="result-header__export-option"
                        onClick={() => onEmailResult()}
                        role="menuitem"
                      >
                        {t("Email")}
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}

            {showAnalysisButton && (
              <div className="result-header__btn-wrap">
                <FormActionButton
                  type="button"
                  variant="secondary"
                  size="sm"
                  iconClassName="bi bi-robot"
                  onClick={onAnalyze}
                  disabled={isAnalysisDisabled}
                >
                  {t("Analiza wyników")}
                </FormActionButton>
              </div>
            )}

            {extraActions}
          </div>
        )}
      </div>

      {subtitle}
    </div>
  );
}